import { z } from 'zod';
import { Id } from './common.js';
/**
 * live-preview-tunnel: agent-served local pages (dev servers, design tools, review servers) opened
 * live from any of the user's devices. The browser and the serving machine agree on a per-session
 * key through the sealed `preview.open` command; afterwards the hub relays opaque `preview.frame`s
 * that carry HTTP requests/responses and WebSocket messages, encrypted end to end
 * (`@viko/crypto` preview-tunnel.ts). The hub sees only the preview id, the machine, sizes and
 * timing. docs/security-multitenant.md "Live preview tunnel".
 */
/** 16 random bytes, base64url. */
export const PreviewId = z.string().regex(/^[A-Za-z0-9_-]{22}$/);
/** Stable per target (machine secret × account × project × port): `p-` + 26 base32 chars. */
export const PreviewLabel = z.string().regex(/^p-[a-z2-7]{26}$/);
/**
 * Preview hosts are `<label><suffix>`, one DNS label deep so a zone's free wildcard certificate
 * covers them: `--pv.viko.build` (production), `--pvs.viko.build` (staging) or `.localhost:8788`
 * (development). A `--<tag>.` suffix keeps preview labels (`p-<26>--pv`, 32 chars) disjoint from
 * every real host of the zone (apex, www, docs, …) and from the other environment's previews.
 */
export const PREVIEW_HOST_SUFFIX_PATTERN = /^(?:--[a-z0-9]{1,8}\.|\.)(?:[a-z0-9-]+\.)*[a-z0-9-]+(?::\d{1,5})?$/;
export function validPreviewHostSuffix(suffix) {
    const s = suffix?.trim().toLowerCase();
    return s && PREVIEW_HOST_SUFFIX_PATTERN.test(s) ? s : undefined;
}
/** The label when `host` (`hostname[:port]`) is exactly `<label><suffix>`, else null. */
export function previewLabelOfHost(host, suffix) {
    const s = validPreviewHostSuffix(suffix);
    const h = host.toLowerCase();
    if (!s || !h.endsWith(s))
        return null;
    const label = h.slice(0, -s.length);
    return PreviewLabel.safeParse(label).success ? label : null;
}
/** Uncompressed P-256 point (65 bytes), base64url. */
const P256Raw = z.string().regex(/^[A-Za-z0-9_-]{87}$/);
const Port = z.number().int().min(1).max(65_535);
/** One opaque tunnel frame, base64url (≤ 64 KiB chunk + header + tag, see PREVIEW_MAX_FRAME_BYTES). */
export const PREVIEW_FRAME_MAX_CHARS = 90_000;
const Frame = z
    .string()
    .min(1)
    .max(PREVIEW_FRAME_MAX_CHARS)
    .regex(/^[A-Za-z0-9_-]+$/);
export const PreviewCaps = z.object({
    /** Sustained transfer rate per session (both directions). */
    bytesPerSecond: z.number().int().positive(),
    /** Total bytes per session before it must be reopened. */
    totalBytes: z.number().int().positive(),
    maxStreams: z.number().int().positive(),
});
export const PreviewOpenResult = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('open'),
        previewId: PreviewId,
        label: PreviewLabel,
        daemonPublic: P256Raw,
        /** What the tunnel forwards to (always loopback on the serving machine). */
        target: z.object({ host: z.string(), port: Port }),
        /** `run`: a listener in the run's process tree/worktree; `shared`: the user shared the port. */
        access: z.enum(['run', 'shared']),
        machineLabel: z.string().max(200),
        expiresAt: z.number().int(),
        idleMs: z.number().int().positive(),
        caps: PreviewCaps,
    }),
    /** The port is listening but not owned by this run: the user may share it (one tap). */
    z.object({
        status: z.literal('needs_share'),
        port: Port,
        /** Name of the listening process, so the user knows what they are sharing. */
        process: z.string().max(200),
    }),
    z.object({
        status: z.literal('denied'),
        reason: z.enum(['not-loopback', 'not-listening', 'unknown-run', 'bad-url', 'disabled']),
    }),
]);
export const PreviewShare = z.object({
    port: Port,
    /** Project the consent is scoped to; absent = any of this machine's previews. */
    projectId: z.string().optional(),
    /** Process name at consent time; a different process on the port is not covered. */
    process: z.string().max(200),
    sharedAt: z.number().int(),
    deviceKeyId: z.string(),
});
export const PREVIEW_COMMAND_PAYLOADS = {
    /** Open a live preview of a loopback URL a run served (or a port the user shared). */
    'preview.open': z.object({
        runId: Id.optional(),
        projectId: Id.optional(),
        url: z.string().min(1).max(4096),
        browserPublic: P256Raw,
    }),
    /** End a preview session now (revokes its key; open streams fail). */
    'preview.close': z.object({ previewId: PreviewId }),
    /** One-time consent: let previews reach a loopback port this machine serves (risky: step-up). */
    'preview.share_port': z.object({ port: Port, projectId: Id.optional() }),
    /** Revoke a shared port; live sessions on it end. */
    'preview.unshare_port': z.object({ port: Port, projectId: Id.optional() }),
    'preview.shares': z.object({}),
    // ---- visual-review: a project's declared review servers and whether each can be opened ----
    'preview.review_servers': z.object({ projectId: Id }),
    // ---- end visual-review ----
};
// ---- hub frames (relay only; payloads are ciphertext) -----------------------------------------
/** Client → hub → daemon. */
export const PreviewClientFrame = z.object({
    type: z.literal('preview.frame'),
    machineId: Id.max(200),
    previewId: PreviewId,
    f: Frame,
});
/** Daemon → hub → the client sockets attached to the preview. */
export const PreviewDaemonFrame = z.object({
    type: z.literal('preview.frame'),
    previewId: PreviewId,
    f: Frame,
});
/** Daemon → hub → clients: the session ended (reason is a code, never content). */
export const PreviewEndFrame = z.object({
    type: z.literal('preview.end'),
    previewId: PreviewId,
    /** `offline`: sent by the hub when the serving machine has no connected daemon. */
    reason: z.enum(['expired', 'idle', 'revoked', 'closed', 'violation', 'cap', 'offline']),
});
// ---- tunnel payloads (inside the encrypted frames; validated by the daemon) -------------------
/** Origin-relative target path: `/…`, never `//host`, no control characters. */
const TargetPath = z
    .string()
    .min(1)
    .max(16_384)
    .regex(/^\/(?![/\\])[^\r\n\0]*$/);
const HeaderList = z.array(z.tuple([z.string().min(1).max(256), z.string().max(16_384)])).max(200);
export const PreviewRequestHead = z.object({
    method: z.string().regex(/^[A-Z][A-Z-]{0,19}$/),
    path: TargetPath,
    headers: HeaderList,
});
export const PreviewWsOpen = z.object({
    path: TargetPath,
    protocols: z.array(z.string().max(256)).max(20),
    headers: HeaderList,
});
export const PreviewWsClose = z.object({
    code: z.number().int().min(1000).max(4999),
    reason: z.string().max(123),
});
//# sourceMappingURL=preview.js.map